import { useState, useEffect } from 'react'
import { Input } from './input'
import { Textarea } from './textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './tabs'
import { Label } from './label'

interface MultilingualInputGroupProps {
  nameValue: { [key: string]: string }
  descriptionValue: { [key: string]: string }
  onNameChange: (value: { [key: string]: string }) => void
  onDescriptionChange: (value: { [key: string]: string }) => void
  namePlaceholder?: string
  descriptionPlaceholder?: string
  nameRequired?: boolean
  nameLabel?: string
  descriptionLabel?: string
  useTextareaForDescription?: boolean
  className?: string
  'data-testid-name'?: string
  'data-testid-description'?: string
}

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
] as const

export function MultilingualInputGroup({
  nameValue,
  descriptionValue,
  onNameChange,
  onDescriptionChange,
  namePlaceholder = '',
  descriptionPlaceholder = '',
  nameRequired = false,
  nameLabel = 'Name',
  descriptionLabel = 'Description',
  useTextareaForDescription = false,
  className,
  'data-testid-name': nameTestId,
  'data-testid-description': descriptionTestId
}: MultilingualInputGroupProps) {
  const [activeTab, setActiveTab] = useState('en')
  
  // Initialize with empty strings for both languages if values are empty
  useEffect(() => {
    if (!nameValue || Object.keys(nameValue).length === 0) {
      onNameChange({ en: '', fr: '' })
    }
    if (!descriptionValue || Object.keys(descriptionValue).length === 0) {
      onDescriptionChange({ en: '', fr: '' })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleNameChange = (langCode: string, inputValue: string) => {
    const newValue = { ...nameValue, [langCode]: inputValue }
    onNameChange(newValue)
  }

  const handleDescriptionChange = (langCode: string, inputValue: string) => {
    const newValue = { ...descriptionValue, [langCode]: inputValue }
    onDescriptionChange(newValue)
  }

  const hasNameValue = (langCode: string) => {
    return nameValue && nameValue[langCode] && nameValue[langCode].trim().length > 0
  }

  const hasDescriptionValue = (langCode: string) => {
    return descriptionValue && descriptionValue[langCode] && descriptionValue[langCode].trim().length > 0
  }

  const isNameValid = () => {
    if (!nameRequired) return true
    // For required fields, at least one language must have a value
    return Object.values(nameValue || {}).some(val => val && val.trim().length > 0)
  }

  return (
    <div className={className}>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          {LANGUAGES.map((lang) => (
            <TabsTrigger 
              key={lang.code} 
              value={lang.code}
              className="flex items-center gap-2 relative"
            >
              <span className="text-lg">{lang.flag}</span>
              <span>{lang.label}</span>
              {(hasNameValue(lang.code) || hasDescriptionValue(lang.code)) && (
                <div className="absolute -top-1 -right-1 h-2 w-2 bg-green-500 rounded-full" />
              )}
            </TabsTrigger>
          ))}
        </TabsList>
        
        {LANGUAGES.map((lang) => (
          <TabsContent key={lang.code} value={lang.code} className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label>{nameLabel} ({lang.label}) {nameRequired && '*'}</Label>
              <Input
                value={nameValue?.[lang.code] || ''}
                onChange={(e) => handleNameChange(lang.code, e.target.value)}
                placeholder={`${namePlaceholder} (${lang.label})`}
                className="w-full"
                data-testid={nameTestId ? `${nameTestId}-${lang.code}` : undefined}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{descriptionLabel} ({lang.label})</Label>
              {useTextareaForDescription ? (
                <Textarea
                  value={descriptionValue?.[lang.code] || ''}
                  onChange={(e) => handleDescriptionChange(lang.code, e.target.value)}
                  placeholder={`${descriptionPlaceholder} (${lang.label})`}
                  className="w-full min-h-20"
                  data-testid={descriptionTestId ? `${descriptionTestId}-${lang.code}` : undefined}
                />
              ) : (
                <Input
                  value={descriptionValue?.[lang.code] || ''}
                  onChange={(e) => handleDescriptionChange(lang.code, e.target.value)}
                  placeholder={`${descriptionPlaceholder} (${lang.label})`}
                  className="w-full"
                  data-testid={descriptionTestId ? `${descriptionTestId}-${lang.code}` : undefined}
                />
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>
      
      {nameRequired && !isNameValid() && (
        <p className="text-sm text-destructive mt-2">
          At least one language must be filled in for {nameLabel.toLowerCase()}
        </p>
      )}
    </div>
  )
}